// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { z } from 'zod';
import { isUndefined } from '@shared/utils';

export const proposalFormSchema = z
  .object({
    name: z.string().min(1),
    reasons: z.array(z.number().int().min(0).max(9)),
    otherReason: z.string().optional(),
  })
  .refine(
    data => {
      // If "other reason" (index 9) is NOT selected, must have at least 1 reason from options 0-8
      const hasOtherReason = data.reasons.includes(9);
      if (!hasOtherReason) {
        const regularReasons = data.reasons.filter(r => r >= 0 && r <= 8);
        return regularReasons.length >= 1;
      }
      return true;
    },
    {
      message: 'At least one reason must be selected',
      path: ['reasons'],
    },
  )
  .refine(
    data => {
      // If "other reason" (index 9) is selected, otherReason is required and must have at least 50 characters
      const hasOtherReason = data.reasons.includes(9);
      if (hasOtherReason) {
        return (
          !isUndefined(data.otherReason) &&
          data.otherReason.length > 0 &&
          data.otherReason.length >= 50
        );
      }
      // If "other reason" is not selected, validation passes (otherReason is optional)
      return true;
    },
    {
      message: 'Other reason must have at least 50 characters when selected',
      path: ['otherReason'],
    },
  );

export type ProposalFormData = z.infer<typeof proposalFormSchema>;
